import { getRequestContext } from "~/lib/requestContext";
import { skipToken, useQuery, UseQueryOptions } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { each } from "lodash";
import { apiClient } from "..";
import { PaginatedQueryParams, PaginatedResponse } from "../types";
import { ContentType } from "./ContentTypes";
import { ImageMedia, Media } from "./Media";
import {
  fetchPaginatedQueryFunction,
  fetchQueryFunction,
  InfiniteQueryOptions,
  makeQueryKey,
  PaginatedPreloadParams,
  PaginatedQueryOptions,
  useInfiniteQuery,
  usePaginatedQuery,
} from "./queries";
import { Tag, TagList } from "./Tags";
import { Social } from "./Socials";

export type ContentAuthor = {
  id: number;
  name?: string;
  email: string;
  avatar?: Media;
  description?: string;
  socials?: Social[];
};

export type Content = {
  id: number;
  created_at: Date;
  updated_at: Date;
  target_id: number;
  target_type: string;
  position: number;
  title: string | null;
  description: string | null;
  short_description: string | null;
  subtitle: string | null;
  text: string | null;
  html: string | null;
  json_data: any | null;
  url: string | null;
  url_text: string | null;
  status: string | null;
  tags: Tag[] | null;
  album: Media[] | null;
  lead_media: Media | null;
  thumbnail_media: ImageMedia | null;
  author: ContentAuthor | null;
  contents: Content[] | null;
  slug: string;
  content_type_slug: string;
};

export type ContentFilter = {
  tags?: { id?: number | number[] };
  search?: string;
  /** Scope contents to a platform (e.g. for ticketure-base-url, ticketure-seller-id). */
  target_id?: number;
  target_type?: string;
};

/** Allowed sort columns; API ignores any other value and applies no sort. */
export type ContentSortColumn =
  | "position"
  | "title"
  | "created_at"
  | "updated_at";

/** order: asc | desc. API default is asc when omitted. */
export type ContentSortOrder = "asc" | "desc";

export type ContentQueryParams = PaginatedQueryParams & {
  filter?: ContentFilter;
  search?: string;
  sort?: ContentSortColumn;
  order?: ContentSortOrder;
};

type ContentResponse = PaginatedResponse<Content>;

export const fetchContent = async (
  contentTypeSlugOrId: string | number,
  contentSlugOrId: string | number,
) =>
  fetchQueryFunction<void, Content>({
    path: `content_types/${contentTypeSlugOrId}/contents/${contentSlugOrId}`,
    resource: ["contents", contentTypeSlugOrId.toString()],
    id: contentSlugOrId,
  }).catch((error) => {
    if (
      isAxiosError(error) &&
      (error.response?.status === 404 || error.status === 404)
    ) {
      return null;
    }
    throw error;
  });

export const useContent = (
  contentTypeSlugOrId: string | number,
  contentSlugOrId?: string | number,
) =>
  useQuery<Content | null, Error>({
    queryKey: makeQueryKey({
      resource: ["contents", contentTypeSlugOrId.toString()],
      id: contentSlugOrId,
    }),
    queryFn:
      contentSlugOrId && contentTypeSlugOrId
        ? () => fetchContent(contentTypeSlugOrId, contentSlugOrId)
        : skipToken,
    enabled: !!contentSlugOrId && !!contentTypeSlugOrId,
  });

export const fetchContents = (
  contentTypeSlug: string,
  params?: ContentQueryParams,
  preload?: PaginatedPreloadParams,
) =>
  fetchPaginatedQueryFunction<ContentQueryParams, ContentResponse>({
    path: `content_types/${contentTypeSlug}/contents`,
    resource: ["contents", contentTypeSlug],
    params,
    preload,
  }).catch((error) => {
    if (isAxiosError(error) && error.status === 404) {
      const response: ContentResponse = {
        records: [],
        next_page: null,
        total_count: 0,
        current_page: 1,
        total_pages: 1,
        per_page: params?.per_page || 10,
      };
      return response;
    }
    throw error;
  });

export const useContents = (
  contentTypeSlug: string,
  params?: ContentQueryParams,
  options?: Omit<PaginatedQueryOptions<Content>, "queryKey" | "queryFn">,
) =>
  usePaginatedQuery(
    ["contents", contentTypeSlug],
    (params) => fetchContents(contentTypeSlug, params),
    params,
    options,
  );

export const useInfiniteContents = (
  contentTypeSlug: string,
  params?: ContentQueryParams,
  options?: Omit<
    InfiniteQueryOptions<Content>,
    "queryKey" | "queryFn" | "initialPageParam" | "getNextPageParam"
  >,
) =>
  useInfiniteQuery(
    ["contents", contentTypeSlug],
    (params) => fetchContents(contentTypeSlug, params),
    params,
    options,
  );

export const fetchContentTypeTags = (
  contentTypeSlug: string | number,
  params?: ContentQueryParams,
) =>
  fetchQueryFunction<ContentQueryParams, TagList>({
    path: `content_types/${contentTypeSlug}/contents/tags`,
    resource: ["tags", "contents", contentTypeSlug.toString()],
    params,
  });

export const useContentTypeTags = (
  contentTypeSlug: string | number,
  params?: ContentQueryParams,
  options?: Omit<UseQueryOptions<TagList, Error>, "queryKey" | "queryFn">,
) =>
  useQuery<TagList, Error>({
    queryKey: ["tags", "contents", contentTypeSlug, params],
    queryFn: () => fetchContentTypeTags(contentTypeSlug, params),
    ...options,
  });

type ContentTypeWithContents = ContentType & {
  contents: Content[];
};

export type ContentByHookParams = { target_type?: string; target_id?: number };

const fetchContentByHook = async (
  hook: string,
  resource?: ContentResource,
  params?: ContentByHookParams,
): Promise<ContentTypeWithContents[]> => {
  if (!resource) {
    console.log("No resource provided");
    return [];
  }

  const { data } = await apiClient().get<Array<ContentTypeWithContents>>(
    `contents/by_hook/${resource}/${hook}`,
    { params },
  );

  return data;
};

export type ContentResource =
  | "Listing"
  | "Region"
  | "Guide"
  | "Itinerary"
  | "Stop"
  | "Event"
  | "Challenge"
  | "Platform"
  | "Communication"
  | "Content"
  | "Task";

export const useContentByHook = (
  hook: string,
  resource?: ContentResource,
  params: ContentByHookParams = {},
) =>
  useQuery<ContentTypeWithContents[], Error>({
    queryKey: ["contents", "by_hook", resource, params || {}, hook],
    queryFn: async () => fetchContentByHook(hook, resource, params),
    enabled: !!resource,
  });

export const fetchContentsForResource = async (
  resource: ContentResource,
  params: ContentByHookParams = {},
): Promise<Content[]> => {
  const { data } = await apiClient().get<
    Record<string, Array<ContentTypeWithContents>>
  >(`contents/by_hook/${resource}`, { params });
  return Object.values(data).flatMap((contentTypes) =>
    contentTypes.flatMap((contentType) => contentType.contents),
  );
};

// Preload content hooks for a given resource and hook slugs.
// If no hook slugs are provided, all hooks for the resource will be preloaded.
export const preloadContentHooks = async (
  resource: ContentResource,
  params: ContentByHookParams & { hook_slugs?: string[] } = {},
) => {
  const { hook_slugs, ...hookParams } = params;
  const { queryClient } = getRequestContext();
  const { data } = await apiClient().get<
    Record<string, Array<ContentTypeWithContents>>
  >(`contents/by_hook/${resource}`, { params });
  each(data, (content_types, slug) => {
    queryClient?.setQueryData(
      ["contents", "by_hook", resource, hookParams, slug],
      content_types,
    );
  });

  return data;
};
